import { useTranslation, Trans } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import VerificationResultCard from '../../components/verificationResultCard/VerificationResultCard';
import ActionContent from './components/ActionContent';
import RegistrationPage from '../../components/layouts/RegistrationPage';

import { ImIdVerificationStatuses } from '../imIdVerificationPending/ImIdVerificationPending';
import type { AccountInfo } from '../../utils/storage';

import imIdLogo from '../../assets/svgs/imid-m.svg';
import { cn } from '../../utils/cn';

const ImIdVerificationResult = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const status = searchParams.get('resultCode') as ImIdVerificationStatuses;
    const account = (location.state as { account?: AccountInfo | null } | null)?.account;
    const { t } = useTranslation();
    const handleBack = () => {
        navigate('/imid-verification');
    };

    const getTryAgainUrl = () => {
        if (status === ImIdVerificationStatuses.CONNECTION_LOST) {
            return '/';
        }

        if (
            [
                ImIdVerificationStatuses.PHONE_NOT_CONNECTED,
                ImIdVerificationStatuses.INCORRECT_SSN,
                ImIdVerificationStatuses.REQUEST_CANCELLED
            ].includes(status)
        ) {
            return '/imid-verification';
        }

        return '/';
    };
    const tryAgainUrl = getTryAgainUrl();

    const messages: Record<
        ImIdVerificationStatuses,
        {
            type: 'success' | 'error';
            title: React.ReactNode;
            description: React.ReactNode;
        }
    > = {
        [ImIdVerificationStatuses.SUCCESS]: {
            type: 'success',
            title: (
                <Trans
                    i18nKey="imIdVerification.approvedWithImId"
                    components={[
                        <img src={imIdLogo} alt="imID" className="w-16 lg:w-[5.125rem]" />,
                    ]}
                    className="max-w-fit"
                />
            ),
            description: t('imIdVerification.identificationSuccessful'),
        },
        [ImIdVerificationStatuses.REQUEST_CANCELLED]: {
            type: 'error',
            title: t('imIdVerification.requestCancelled'),
            description: t('imIdVerification.notApprovedFromPhone'),
        },
        [ImIdVerificationStatuses.PHONE_NOT_CONNECTED]: {
            type: 'error',
            title: t('imIdVerification.numberNotLinked'),
            description: t('imIdVerification.enterPhoneOrBiometric'),
        },
        [ImIdVerificationStatuses.CONNECTION_LOST]: {
            type: 'error',
            title: t('imIdVerification.connectionLost'),
            description: t('imIdVerification.approvalNotCompleted'),
        },
        [ImIdVerificationStatuses.INCORRECT_SSN]: {
            type: 'error',
            title: t('imIdVerification.incorrectSSN'),
            description: t('imIdVerification.ssnMismatch'),
        },
        [ImIdVerificationStatuses.GENERAL_ERROR]: {
            type: 'error',
            title: t('imIdVerification.generalErrorTitle'),
            description: t('imIdVerification.generalErrorDescription'),
        },
    };

    const message = messages[status] || messages[ImIdVerificationStatuses.GENERAL_ERROR];

    return (
        <RegistrationPage
            headerProps={{
                showBack: status !== ImIdVerificationStatuses.SUCCESS,
                onBack: handleBack,
            }}
            contentClassName="items-center flex-1"
            contentWrapperClassName='flex-1'
        >
            <div className="flex-1 flex flex-col items-center text-center gap-10 md:max-w-[32.5rem] w-full">
                <VerificationResultCard
                    type={message.type}
                    title={message.title}
                    description={message.description}
                    contentClassName={cn(status !== ImIdVerificationStatuses.SUCCESS && 'mt-auto md:mt-0')}
                >
                    <ActionContent
                        status={status}
                        tryAgainUrl={tryAgainUrl}
                        account={account}
                    />
                </VerificationResultCard>
            </div>
        </RegistrationPage>
    );
};

export default ImIdVerificationResult;
